import type { PropsWithChildren } from 'react';

import { FlagValue } from '@openfeature/core';
import {
  OpenFeatureProvider,
  useFlag as useOpenFeatureFlag,
} from '@openfeature/react-sdk';
import {
  type EvaluationContext,
  type EvaluationContextValue,
  OpenFeature,
  type Provider,
  type ResolutionDetails,
} from '@openfeature/web-sdk';

import { createConfidenceWebProvider } from '@spotify-confidence/openfeature-web-provider';
import { Confidence } from '@spotify-confidence/sdk';

import type {
  FlagEvaluationAttributes,
  FlagValueResolver,
} from '../../../../interceptors';

export const OpenFeatureBoundary = ({ children }: PropsWithChildren) => (
  <OpenFeatureProvider suspendUntilReady>{children}</OpenFeatureProvider>
);

export const useOpenFeatureFlagValue: FlagValueResolver = (
  flag,
  defaultValue,
) =>
  useOpenFeatureFlag(flag, defaultValue as FlagValue)
    .value as typeof defaultValue;

const toResolutionDetails = <T,>(
  evaluation: ResolutionDetails<T>,
): ResolutionDetails<T> => {
  const details: ResolutionDetails<T> = { value: evaluation.value };

  if (evaluation.variant !== undefined) {
    details.variant = evaluation.variant;
  }
  if (evaluation.reason !== undefined) {
    details.reason = evaluation.reason;
  }
  if (evaluation.errorCode !== undefined) {
    details.errorCode = evaluation.errorCode;
  }
  if (evaluation.errorMessage !== undefined) {
    details.errorMessage = evaluation.errorMessage;
  }
  if (evaluation.flagMetadata !== undefined) {
    details.flagMetadata = evaluation.flagMetadata;
  }

  return details;
};

const withDefaultsOnResolutionFailure = (provider: Provider): Provider => {
  const initialize = provider.initialize?.bind(provider);
  const onContextChange = provider.onContextChange?.bind(provider);

  return Object.assign(provider, {
    initialize: async (context?: EvaluationContext, domain?: string) => {
      try {
        await initialize?.(context, domain);
      } catch {
        return;
      }
    },
    onContextChange: async (
      oldContext: EvaluationContext,
      newContext: EvaluationContext,
    ) => {
      try {
        await onContextChange?.(oldContext, newContext);
      } catch {
        return;
      }
    },
  });
};

const withResolutionDetails = (provider: Provider): Provider => {
  const resolveBooleanEvaluation =
    provider.resolveBooleanEvaluation.bind(provider);
  const resolveStringEvaluation =
    provider.resolveStringEvaluation.bind(provider);
  const resolveNumberEvaluation =
    provider.resolveNumberEvaluation.bind(provider);
  const resolveObjectEvaluation =
    provider.resolveObjectEvaluation.bind(provider);

  return Object.assign(provider, {
    resolveBooleanEvaluation: (
      ...args: Parameters<Provider['resolveBooleanEvaluation']>
    ) => toResolutionDetails(resolveBooleanEvaluation(...args)),
    resolveStringEvaluation: (
      ...args: Parameters<Provider['resolveStringEvaluation']>
    ) => toResolutionDetails(resolveStringEvaluation(...args)),
    resolveNumberEvaluation: (
      ...args: Parameters<Provider['resolveNumberEvaluation']>
    ) => toResolutionDetails(resolveNumberEvaluation(...args)),
    resolveObjectEvaluation: (
      ...args: Parameters<Provider['resolveObjectEvaluation']>
    ) => toResolutionDetails(resolveObjectEvaluation(...args)),
  });
};

export const bindOpenFeature = (confidence: Confidence): void => {
  OpenFeature.setProvider(
    withResolutionDetails(
      withDefaultsOnResolutionFailure(createConfidenceWebProvider(confidence)),
    ),
  );
};

const toEvaluationContext = (
  attributes: FlagEvaluationAttributes,
): EvaluationContext => {
  const context: Record<string, EvaluationContextValue> = {};

  for (const [key, value] of Object.entries(attributes)) {
    if (value !== undefined) {
      context[key] = value;
    }
  }

  return context;
};

const isSameEvaluationContext = (
  current: EvaluationContext,
  next: EvaluationContext,
): boolean => {
  const currentKeys = Object.keys(current);

  return (
    currentKeys.length === Object.keys(next).length &&
    currentKeys.every(key => key in next && Object.is(current[key], next[key]))
  );
};

export const setEvaluationContext = (
  attributes: FlagEvaluationAttributes,
): void => {
  const context = toEvaluationContext(attributes);

  if (isSameEvaluationContext(OpenFeature.getContext(), context)) {
    return;
  }

  OpenFeature.setContext(context);
};
